import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useUser } from 'app/user';
import { getSubscription } from 'utils/api';

const PLANS = [
  {
    key: 'free',
    name: 'FREE',
    price: '0',
    originalPrice: null,
    description: '무료 체험 사용자',
    features: [
      '서울시 강남구 한정',
      '기본 필터 (평형, 세대수, 하락률 등)',
      '급매 자동알림 X',
      '갭메우기 기능 X',
    ],
    color: '#a3aed0',
  },
  {
    key: 'basic',
    name: 'BASIC',
    price: '9,900',
    originalPrice: '12,000',
    description: '내집마련 / 갈아타기 준비',
    features: [
      '전국 지역 조회',
      '기본 필터 (평형, 세대수, 하락률 등)',
      '급매 자동알림 (5개 구, 주1회)',
      '갭메우기 기능 X',
    ],
    color: '#644ded',
  },
  {
    key: 'premium',
    name: 'PREMIUM',
    price: '19,900',
    originalPrice: '26,000',
    description: '내집마련 / 갈아타기 / 다주택자',
    features: [
      '전국 지역 조회',
      '기본 필터 (평형, 세대수, 하락률 등)',
      '급매 자동알림 (10개 구, 주3회)',
      '갭메우기 가성비 단지 기능',
      '나눔마린 질문권 (분기 1회)',
    ],
    color: '#ff6b35',
  },
];

const Pricing = () => {
  const navigate = useNavigate();
  const { plan: currentPlan } = useUser();
  const [isCancelled, setIsCancelled] = useState(false);
  const [isLegacy, setIsLegacy] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await getSubscription();
        if (res.statusCode === 200 && res.results) {
          setIsCancelled(res.results.status === 'cancelled');
          setIsLegacy(res.results.legacy === true);
          setIsFirstTime(res.results.isFirstTime === true);
        }
      } catch {
        // ignore
      }
    };
    fetchStatus();
  }, []);

  const isCurrentPlan = (planKey) =>
    planKey === currentPlan && !isCancelled && !isLegacy;

  const handleSelect = (planKey) => {
    if (planKey === 'free') return;
    if (isLegacy) {
      // legacy 유저는 어떤 플랜이든 새로 결제
      navigate(`/subscribe/${planKey}`);
      return;
    }
    if (isCurrentPlan(planKey)) {
      navigate('/subscription');
    } else if (isCancelled || currentPlan === 'free') {
      navigate(`/subscribe/${planKey}`);
    } else {
      // 활성 구독에서 다른 플랜 → 구독관리(업그레이드)
      navigate('/subscription');
    }
  };

  const getButtonText = (planKey) => {
    if (planKey === 'free') return '무료';
    if (isLegacy) return '결제 이동';
    if (isCurrentPlan(planKey)) return '현재 플랜';
    if (isCancelled) return '구독하기';
    if (currentPlan !== 'free' && planKey !== 'free') return '플랜 변경';
    return '구독하기';
  };

  return (
    <Container>
      <Title>요금제 선택</Title>
      <Subtitle>나에게 맞는 플랜을 선택하세요</Subtitle>
      {isLegacy && (
        <LegacyBanner>
          결제 방식이 사이트 내 결제로 변경되었습니다. 아래에서 플랜을 선택하여
          결제해주시고, 기존 외부 정기결제는 해제해주세요.
        </LegacyBanner>
      )}
      {isFirstTime && (
        <FirstTimeBanner>
          <strong>첫 구독 1+1 이벤트!</strong> 1개월 결제하시면 2개월 동안 이용
          가능합니다.
        </FirstTimeBanner>
      )}
      <PlanGrid>
        {PLANS.map((p) => (
          <PlanCard key={p.key} isActive={isCurrentPlan(p.key)}>
            <PlanBadge color={p.color}>{p.name}</PlanBadge>
            <PlanPrice>
              {p.originalPrice && (
                <OriginalPrice>{p.originalPrice}원</OriginalPrice>
              )}
              <CurrentPrice>
                {p.price}
                <span>원/월</span>
              </CurrentPrice>
            </PlanPrice>
            <PlanDescription>{p.description}</PlanDescription>
            {isFirstTime && p.key !== 'free' && (
              <BonusTag>첫 결제 1+1 (2개월 이용)</BonusTag>
            )}
            <FeatureList>
              {p.features.map((f, i) => (
                <Feature key={i}>{f}</Feature>
              ))}
            </FeatureList>
            <SelectButton
              color={p.color}
              disabled={(!isLegacy && isCurrentPlan(p.key)) || p.key === 'free'}
              onClick={() => handleSelect(p.key)}
            >
              {getButtonText(p.key)}
            </SelectButton>
          </PlanCard>
        ))}
      </PlanGrid>
    </Container>
  );
};

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 60px 20px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #8f9bba;
  margin-bottom: 48px;
`;

const LegacyBanner = styled.div`
  background-color: #fff3cd;
  border: 1px solid #ffe69c;
  border-radius: 8px;
  padding: 14px 20px;
  margin-bottom: 32px;
  font-size: 14px;
  color: #856404;
  line-height: 1.5;
  text-align: left;
`;

const FirstTimeBanner = styled.div`
  background: linear-gradient(135deg, #fff0f6 0%, #ffe3ee 100%);
  border: 1px solid #ff6b9a;
  border-radius: 12px;
  padding: 16px 22px;
  margin-bottom: 32px;
  font-size: 15px;
  color: #c2185b;
  line-height: 1.5;
  text-align: center;

  strong {
    font-weight: 700;
    color: #ad1457;
  }
`;

const BonusTag = styled.div`
  background-color: #fff0f6;
  color: #c2185b;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #ffc1d9;
`;

const PlanGrid = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PlanCard = styled.div`
  background: #fff;
  border: 2px solid ${({ isActive }) => (isActive ? '#644ded' : '#e4e4e4')};
  border-radius: 16px;
  padding: 32px 24px;
  width: 280px;
  text-align: center;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ isActive }) =>
    isActive ? '0 4px 20px rgba(100, 77, 237, 0.15)' : '0 2px 8px #eee'};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  }
`;

const PlanBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 700;
  padding: 4px 16px;
  border-radius: 20px;
  color: #fff;
  background-color: ${({ color }) => color};
  margin-bottom: 20px;
`;

const PlanPrice = styled.div`
  margin-bottom: 12px;
`;

const OriginalPrice = styled.div`
  font-size: 14px;
  color: #a3aed0;
  text-decoration: line-through;
  margin-bottom: 4px;
`;

const CurrentPrice = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #232d42;

  span {
    font-size: 14px;
    font-weight: 400;
    color: #8f9bba;
  }
`;

const PlanDescription = styled.p`
  font-size: 14px;
  color: #8f9bba;
  margin-bottom: 24px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 24px;
  text-align: left;
  flex: 1;
`;

const Feature = styled.li`
  font-size: 13px;
  color: #4a5568;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const SelectButton = styled.button`
  width: 100%;
  padding: 12px 0;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #fff;
  background-color: ${({ color, disabled }) => (disabled ? '#e4e4e4' : color)};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

export default Pricing;
